import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getCommissioniAffiliato, getCommissioniOperatori, getCommissioniPipeline, getCommissioniSerie,
} from "@/api/affiliato";
import { DateRangePicker } from "@/components/DateRangePicker";
import { KpiCard, RankBar, TrendLine } from "@/components/charts";
import { ErrorState } from "@/components/ErrorState";
import { formatDate, formatMonth } from "@/lib/dates";
import { presetRange, type Range } from "@/lib/periodo";
import type { CommissioniResponse, OperatoriResponse, PipelineResponse, SerieResponse } from "@/types";

const eur = (n: number) => "€" + n.toLocaleString("it-IT", { minimumFractionDigits: 2 });

export function Commissioni() {
  const [range, setRange] = useState<Range>(useMemo(() => presetRange("corrente"), []));
  const [comm, setComm] = useState<CommissioniResponse | null>(null);
  const [serie, setSerie] = useState<SerieResponse | null>(null);
  const [ops, setOps] = useState<OperatoriResponse | null>(null);
  const [pipe, setPipe] = useState<PipelineResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    Promise.all([
      getCommissioniAffiliato(range.from, range.to),
      getCommissioniSerie(range.from, range.to),
      getCommissioniOperatori(range.from, range.to),
      getCommissioniPipeline(range.from, range.to),
    ]).then(([c, s, o, p]) => {
      if (cancelled) return;
      setComm(c); setSerie(s); setOps(o); setPipe(p);
    }).catch(() => { if (!cancelled) setError("Impossibile caricare le commissioni."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range]);

  const sparkTotals = serie?.punti.map((p) => p.total) ?? [];
  const sparkCounts = serie?.punti.map((p) => p.count) ?? [];

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text">Commissioni</h2>
          <p className="text-sm text-muted-foreground">
            Andamento dei tuoi gettoni — per data di accettazione
          </p>
        </div>
        <DateRangePicker value={range} onChange={setRange} />
      </div>

      {error ? <ErrorState message={error} />
        : loading || !comm || !serie || !ops || !pipe ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-28 bg-surface rounded-lg animate-pulse" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiCard label="Commissioni periodo" value={eur(comm.total)} spark={sparkTotals} />
              <KpiCard label="Contratti" value={String(comm.rows.length)} spark={sparkCounts} />
              <KpiCard label="Operatori" value={String(ops.rows.length)} />
              <KpiCard label="Media / contratto" value={eur(comm.rows.length ? comm.total / comm.rows.length : 0)} />
            </div>

            <div className="bg-card rounded-lg border border-border p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-text">Gettoni in maturazione</span>
                <span className="text-xs text-muted-foreground">pratiche aperte non ancora chiuse</span>
              </div>
              <div className="flex items-end gap-8">
                <div><div className="text-2xl font-bold text-text">{eur(pipe.maturato)}</div><div className="text-xs text-muted-foreground">maturati</div></div>
                <div><div className="text-2xl font-bold text-blue-700">{pipe.pratiche_pipeline}</div><div className="text-xs text-muted-foreground">pratiche in pipeline</div></div>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
              <div className="bg-card rounded-lg border border-border p-4 lg:col-span-2">
                <div className="text-sm font-semibold text-text mb-3">
                  Andamento — {range.from === range.to ? `${formatMonth(range.from)} (per giorno)` : `${range.from} → ${range.to}`}
                </div>
                <TrendLine data={serie.punti} />
              </div>
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="text-sm font-semibold text-text mb-3">Commissioni per operatore</div>
                <RankBar data={ops.rows.map((o) => ({ label: o.operatore_name ?? "—", value: o.totale }))} />
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="px-4 py-3 border-b border-border text-sm font-semibold text-text">Classifica operatori</div>
              <table className="w-full text-sm">
                <thead className="bg-surface text-muted-foreground"><tr>
                  <th className="text-left py-2 px-4 font-medium">Operatore</th>
                  <th className="text-right py-2 px-4 font-medium">Pratiche</th>
                  <th className="text-right py-2 px-4 font-medium">Totale</th>
                </tr></thead>
                <tbody>
                  {ops.rows.map((o) => (
                    <tr key={o.operatore_id ?? o.operatore_name} className="border-t border-border">
                      <td className="py-2 px-4 text-text">{o.operatore_name ?? "—"}</td>
                      <td className="py-2 px-4 text-right text-text">{o.num_pratiche}</td>
                      <td className="py-2 px-4 text-right font-medium text-text">{eur(o.totale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {comm.rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nessuna commissione nel periodo selezionato.</p>
            ) : (
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-surface text-muted-foreground"><tr>
                    <th className="text-left py-2 px-4 font-medium">Data</th>
                    <th className="text-left py-2 px-4 font-medium">Contratto</th>
                    <th className="text-left py-2 px-4 font-medium">Cliente</th>
                    <th className="text-left py-2 px-4 font-medium">Operatore</th>
                    <th className="text-right py-2 px-4 font-medium">Quota</th>
                  </tr></thead>
                  <tbody>
                    {comm.rows.map((r) => (
                      <tr key={r.opportunity_id} onClick={() => navigate(`/agenzia/pratiche/${r.opportunity_id}`)}
                        className="border-t border-border cursor-pointer hover:bg-surface">
                        <td className="py-2 px-4 text-text">{formatDate(r.close_date)}</td>
                        <td className="py-2 px-4 text-text">{r.opportunity_name}</td>
                        <td className="py-2 px-4 text-muted-foreground">{r.cliente_name ?? "—"}</td>
                        <td className="py-2 px-4"><span className="text-xs bg-surface px-2 py-0.5 rounded-full">{r.operatore_name ?? "—"}</span></td>
                        <td className="py-2 px-4 text-right font-medium text-text">{eur(r.quota_agenzia)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
    </div>
  );
}
